import { fetchPrivateApi } from '@/lib/api/apiServiceBase.ts';
import { TableDefinitionDto } from '@/components/table/types/TableDefinitionDto.ts';
import { PageRequest } from '@/components/table/types/PageRequest.ts';
import { FilterCriteriaDto } from '@/components/table/types/FilterCriteriaDto.tsx';
import { PagedDto } from '@/components/table/types/PagedDto.ts';
import { buildPageRequestParams } from '@/components/table/types/TableUtil.ts';
import { TourExpenseDto } from '@/features/dashboard/expenses/types/TourExpenseDto.ts';
import { TourExpenseListDto } from '@/features/dashboard/expenses/types/TourExpenseListDto.ts';
import {
  CreateTourExpenseRequestDto,
  UpdateTourExpenseRequestDto,
} from '@/features/dashboard/expenses/types/ExpenseRequest.ts';
import {
  MatchExpenseRequestDto,
  PayTourExpenseRequestDto,
} from '@/features/dashboard/expenses/types/PayExpenseRequest.ts';

const expensesApi = {
  // Per tour
  getAll:
    (tourId: string) =>
    async (
      pageRequest: PageRequest,
      filterCriteria: FilterCriteriaDto,
    ): Promise<PagedDto<TourExpenseListDto>> => {
      const params = buildPageRequestParams(pageRequest);
      return await fetchPrivateApi<PagedDto<TourExpenseListDto>>(
        'POST',
        `tours/${tourId}/expenses/all?${params.toString()}`,
        filterCriteria,
      );
    },

  getTableDefinition:
    (tourId: string) => async (): Promise<TableDefinitionDto> => {
      return await fetchPrivateApi<TableDefinitionDto>(
        'GET',
        `tours/${tourId}/expenses/table-definition`,
      );
    },

  getById: async (
    tourId: string,
    expenseId: string,
  ): Promise<TourExpenseDto> => {
    return await fetchPrivateApi<TourExpenseDto>(
      'GET',
      `tours/${tourId}/expenses/${expenseId}`,
    );
  },

  create: async (dto: CreateTourExpenseRequestDto): Promise<TourExpenseDto> => {
    return await fetchPrivateApi<TourExpenseDto>(
      'POST',
      `tours/${dto.tourId}/expenses`,
      dto,
    );
  },

  update: async (
    tourId: string,
    expenseId: string,
    dto: UpdateTourExpenseRequestDto,
  ): Promise<TourExpenseDto> => {
    return await fetchPrivateApi<TourExpenseDto>(
      'PUT',
      `tours/${tourId}/expenses/${expenseId}`,
      dto,
    );
  },

  remove: async (tourId: string, expenseId: string): Promise<void> => {
    return await fetchPrivateApi<void>(
      'DELETE',
      `tours/${tourId}/expenses/${expenseId}`,
    );
  },

  /** Rejestruje faktyczną zapłatę. Zastąpiło dawne mark-paid. */
  pay: async (
    tourId: string,
    expenseId: string,
    dto: PayTourExpenseRequestDto,
  ): Promise<TourExpenseDto> => {
    return await fetchPrivateApi<TourExpenseDto>(
      'POST',
      `tours/${tourId}/expenses/${expenseId}/pay`,
      dto,
    );
  },

  /** Anulowanie odpina zapłaty — transakcje wracają do puli niezaalokowanych. */
  cancel: async (
    tourId: string,
    expenseId: string,
  ): Promise<TourExpenseDto> => {
    return await fetchPrivateApi<TourExpenseDto>(
      'POST',
      `tours/${tourId}/expenses/${expenseId}/cancel`,
    );
  },

  matchToBankTransaction: async (
    tourId: string,
    expenseId: string,
    dto: MatchExpenseRequestDto,
  ): Promise<TourExpenseDto> => {
    return await fetchPrivateApi<TourExpenseDto>(
      'POST',
      `tours/${tourId}/expenses/${expenseId}/match`,
      dto,
    );
  },

  // Global
  getAllGlobal: async (
    pageRequest: PageRequest,
    filterCriteria: FilterCriteriaDto,
  ): Promise<PagedDto<TourExpenseListDto>> => {
    const params = buildPageRequestParams(pageRequest);
    return await fetchPrivateApi<PagedDto<TourExpenseListDto>>(
      'POST',
      `expenses/all?${params.toString()}`,
      filterCriteria,
    );
  },

  getGlobalTableDefinition: async (): Promise<TableDefinitionDto> => {
    return await fetchPrivateApi<TableDefinitionDto>(
      'GET',
      'expenses/table-definition',
    );
  },
};

export default expensesApi;
